import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { AppProvider } from "./context/AppContext";
import { isAppPath, normalizeAppPath, type AppPath } from "./lib/navigation";
import type { BootstrapData } from "./types";
import { AppShell } from "./components/AppShell";
import { AuthScreen } from "./pages/AuthScreen";
import { AccountsPage } from "./pages/AccountsPage";
import { BillsPage } from "./pages/BillsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { GoalsPage } from "./pages/GoalsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TransactionsPage } from "./pages/TransactionsPage";

type AuthState = { loading: boolean; setupRequired: boolean; user: { email: string } | null; error: string };
function currentAppPath(): AppPath {
  return normalizeAppPath(window.location.pathname);
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ loading: true, setupRequired: false, user: null, error: "" });
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [path, setPath] = useState<AppPath>(currentAppPath);

  const navigate = useCallback((nextPath: string) => {
    const safePath = normalizeAppPath(nextPath);
    if (window.location.pathname !== safePath) window.history.pushState(null, "", safePath);
    setPath(safePath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  async function load() {
    try {
      const status = await api.authStatus();
      setAuth({ loading: false, setupRequired: status.setupRequired, user: status.user, error: "" });
      if (status.user) setBootstrap(await api.bootstrap());
    } catch (error) {
      setAuth({ loading: false, setupRequired: false, user: null, error: error instanceof Error ? error.message : "Unable to connect" });
    }
  }

  useEffect(() => {
    let active = true;
    api.authStatus().then(async (status) => {
      const data = status.user ? await api.bootstrap() : null;
      if (!active) return;
      setAuth({ loading: false, setupRequired: status.setupRequired, user: status.user, error: "" });
      setBootstrap(data);
    }).catch((error) => {
      if (active) setAuth({ loading: false, setupRequired: false, user: null, error: error instanceof Error ? error.message : "Unable to connect" });
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const syncPath = () => setPath(currentAppPath());
    window.addEventListener("popstate", syncPath);
    if (window.location.pathname !== path) window.history.replaceState(null, "", path);
    return () => window.removeEventListener("popstate", syncPath);
  }, [path]);

  useEffect(() => {
    function followInternalLink(event: globalThis.MouseEvent) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>("a[href]");
      if (!link || link.origin !== window.location.origin || link.target || link.hasAttribute("download") || !isAppPath(link.pathname)) return;
      event.preventDefault();
      navigate(link.pathname);
    }
    document.addEventListener("click", followInternalLink);
    return () => document.removeEventListener("click", followInternalLink);
  }, [navigate]);

  if (auth.loading) return <div className="launch-screen"><div className="brand-mark">D<span /></div><p>Preparing your money view…</p></div>;
  if (!auth.user || !bootstrap) return <AuthScreen setupRequired={auth.setupRequired} connectionError={auth.error} onAuthenticated={load} />;

  const page = path === "/transactions" ? <TransactionsPage />
    : path === "/accounts" ? <AccountsPage />
      : path === "/bills" ? <BillsPage />
        : path === "/goals" ? <GoalsPage />
          : path === "/settings" ? <SettingsPage onLogout={load} />
            : <DashboardPage />;

  return (
    <AppProvider initial={bootstrap}>
      <AppShell currentPath={path} navigate={navigate}>
        {page}
      </AppShell>
    </AppProvider>
  );
}
